import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { AlertCircle, ArrowUpRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { useDispatch, useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import FilterReusableComp from "../../../../components/FilterReusableComp";
import { Button } from "../../../../components/custom/custom-button";
import DateRangeComponent from "../../../../components/fragment/date-range/DateRangeComponent";
import ReuseModal from "../../../../components/fragment/modal/ReuseModal";
import PaginationFragment from "../../../../components/fragment/pagination/PaginationFragment";
import {
  RazorBadgeTwo,
  RazorNumberFormat,
  RazorTable,
  RazorTableRow,
} from "../../../../components/razor-reuse";
import NoContentPage from "../../../../layout/dashboard/no-content/NoContentPage";
import {
  getReferralEarningsChart,
  getReferralList,
  getReferralOverviewStat,
  getReferralProgramStatus,
  getReferralRewardDistribution,
  publishReferralProgram,
  unpublishReferralProgram,
} from "../../../../redux/referral";
import RootState, {
  ReferralEarningsChartInterval,
  ReferralItemStatus,
  ReferralOverviewStats,
} from "../../../../redux/type";
import { formatTime } from "../../../../utils/Helper";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
);

interface StatCardData {
  label: string;
  value: string;
  delta?: string;
  loading?: boolean;
}

type ReferralStatKey = keyof ReferralOverviewStats;

interface StatConfig {
  key: ReferralStatKey;
  label: string;
  changeField?: "change_percent" | "change_pp";
  changeSuffix: string;
  format: (value: number) => string;
}

const STAT_CONFIG: StatConfig[] = [
  {
    key: "total_referrals",
    label: "Total Referrals",
    changeField: "change_percent",
    changeSuffix: "vs last month",
    format: (value) =>
      RazorNumberFormat(value, { hideDecimal: true, hideSymbol: true }),
  },
  {
    key: "qualified_referrals",
    label: "Qualified Referrals",
    changeField: "change_percent",
    changeSuffix: "vs last month",
    format: (value) =>
      RazorNumberFormat(value, { hideDecimal: true, hideSymbol: true }),
  },
  {
    key: "pending_referrals",
    label: "Pending Referrals",
    changeField: "change_percent",
    changeSuffix: "vs last month",
    format: (value) =>
      RazorNumberFormat(value, { hideDecimal: true, hideSymbol: true }),
  },
  {
    key: "rewards_issued",
    label: "Rewards Issued",
    changeField: "change_percent",
    changeSuffix: "vs last month",
    format: (value) =>
      RazorNumberFormat(value, { hideDecimal: true, hideSymbol: true }),
  },
  {
    key: "conversion_rate",
    label: "Conversion Rate",
    changeField: "change_pp",
    changeSuffix: "pp vs last month",
    format: (value) => `${value.toFixed(2)}%`,
  },
  {
    key: "average_reward_per_referral",
    label: "Average Reward/ Referral",
    changeSuffix: "vs last month",
    format: (value) => value.toFixed(2),
  },
];

const REWARD_DISTRIBUTION_COLORS: Record<string, string> = {
  USD: "#16A34A",
  EUR: "#F5B400",
  GBP: "#2563EB",
  NGN: "#EC4899",
  CAD: "#DC2626",
};
const DEFAULT_REWARD_DISTRIBUTION_COLOR = "#734792";

const EARNINGS_INTERVAL_OPTIONS: {
  label: string;
  value: ReferralEarningsChartInterval;
}[] = [
  { label: "Daily", value: "day" },
  { label: "Monthly", value: "month" },
  { label: "Yearly", value: "year" },
];

const REFERRAL_STATUS_SELECT_LIST: {
  label: string;
  value: ReferralItemStatus | "";
}[] = [
  { label: "Pending", value: "pending" },
  { label: "Qualified", value: "qualified" },
  { label: "Rewarded", value: "rewarded" },
  { label: "Expired", value: "expired" },
  { label: "All status", value: "" },
];

const REFERRAL_STATUS_BADGE: Record<
  ReferralItemStatus,
  { label: string; badgeClass: string }
> = {
  pending: { label: "Pending", badgeClass: "pending" },
  qualified: { label: "Qualified", badgeClass: "info" },
  rewarded: { label: "Rewarded", badgeClass: "success" },
  expired: { label: "Expired", badgeClass: "failed" },
};

const REFERRALS_PER_PAGE = 15;

function StatCard({ label, value, delta, loading }: StatCardData) {
  return (
    <div className="bg-white border border-[#E4E4E7] rounded-[8px] p-[16px]">
      <p className="text-[14px] text-[#734792] font-medium">{label}</p>
      {loading ? (
        <Skeleton width={80} height={28} style={{ marginTop: "8px" }} />
      ) : (
        <p className="text-[28px] font-bold text-[#191C1F] mt-[8px]">{value}</p>
      )}
      {!loading && delta && (
        <p className="inline-flex items-center gap-[4px] text-[#16A34A] text-[13px] mt-[8px]">
          <ArrowUpRight className="size-[14px]" />
          <span>{delta}</span>
        </p>
      )}
    </div>
  );
}

export default function ReferralOverview() {
  const dispatch = useDispatch();
  const {
    overview_stats,
    loading_overview,
    reward_distribution,
    loading_reward_distribution,
    program_status,
    loading_publish_toggle,
    referrals,
    loading_referrals,
    earnings_chart,
    loading_earnings_chart,
  } = useSelector((state: RootState) => state.referral);
  const { user } = useSelector((state: RootState) => state.auth);
  const canSetProgramStatus = !!user?.permissions?.includes(
    "set_referral_setting",
  );

  const [filterStatus, setFilterStatus] = useState<any>("");
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [page, setPage] = useState(1);
  const [publishModal, setPublishModal] = useState(false);
  const [earningsInterval, setEarningsInterval] =
    useState<ReferralEarningsChartInterval>("month");

  const [rangeStart, rangeEnd] = dateRange ? dateRange.split(",") : ["", ""];

  useEffect(() => {
    dispatch(getReferralOverviewStat() as any);
    dispatch(getReferralRewardDistribution() as any);
    dispatch(getReferralProgramStatus() as any);
  }, [dispatch]);

  useEffect(() => {
    dispatch(getReferralEarningsChart({ interval: earningsInterval }) as any);
  }, [dispatch, earningsInterval]);

  useEffect(() => {
    dispatch(
      getReferralList({
        page,
        per_page: REFERRALS_PER_PAGE,
        status: filterStatus?.value,
        search,
        start_date: rangeStart,
        end_date: rangeEnd,
      }) as any,
    );
  }, [dispatch, page, filterStatus, search, rangeStart, rangeEnd]);

  const goToFirstPage = () => setPage(1);

  const isProgramPublished = program_status?.is_published || false;

  const handleTogglePublish = async () => {
    const action = isProgramPublished
      ? unpublishReferralProgram
      : publishReferralProgram;
    const result: any = await dispatch(action() as any);
    if (result?.payload?.data?.status) {
      setPublishModal(false);
    }
  };

  const isInitialLoad = loading_overview && !overview_stats;

  const statCards: StatCardData[] = STAT_CONFIG.map((config) => {
    const metric = overview_stats?.[config.key];
    const changeValue = config.changeField
      ? metric?.[config.changeField]
      : undefined;

    return {
      label: config.label,
      value: metric ? config.format(metric.value) : "0",
      delta:
        typeof changeValue === "number"
          ? `${changeValue > 0 ? "+" : ""}${changeValue} ${config.changeSuffix}`
          : undefined,
      loading: isInitialLoad,
    };
  });

  const isEarningsChartLoading = loading_earnings_chart && !earnings_chart;
  const earningsChartLabels = (earnings_chart || []).map(
    (item) => item?.label || "",
  );
  const earningsChartAmounts = (earnings_chart || []).map(
    (item) => item?.amount || 0,
  );

  const isRewardDistributionLoading =
    loading_reward_distribution && !reward_distribution;
  const maxRewardAmount = Math.max(
    1,
    ...(reward_distribution || []).map((item) => item?.amount || 0),
  );
  const rewardDistributionRows = (reward_distribution || []).map((item) => ({
    currency: item?.currency_code || "",
    amount: RazorNumberFormat(item?.amount || 0, {
      symbol: item?.currency_code,
      hideDecimal: true,
    }),
    widthPercent: ((item?.amount || 0) / maxRewardAmount) * 100,
    color:
      REWARD_DISTRIBUTION_COLORS[item?.currency_code || ""] ||
      DEFAULT_REWARD_DISTRIBUTION_COLOR,
  }));

  const isInitialReferralLoad = loading_referrals && !referrals;
  const referralRows = referrals?.data || [];

  return (
    <div className="py-[16px] space-y-[20px]">
      {/* header row */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[20px] font-bold text-[#1E1E1E]">
            Referral Program
          </p>
          <p className="text-[14px] text-[#758089] mt-[4px]">
            Manage and monitor referral program performance
          </p>
        </div>
        <div className="flex items-center gap-[12px]">
          <span
            className={`inline-flex items-center gap-[6px] rounded-[999px] px-[12px] py-[6px] text-[13px] font-medium ${
              isProgramPublished
                ? "bg-[#DCFCE7] text-[#16A34A]"
                : "bg-[#F4F4F5] text-[#3F3F46]"
            }`}
          >
            <span
              className={`size-[6px] rounded-full ${
                isProgramPublished ? "bg-[#16A34A]" : "bg-[#3F3F46]"
              }`}
            />
            {isProgramPublished ? "Active" : "Inactive"}
          </span>
          {canSetProgramStatus && (
            <Button
              variant={isProgramPublished ? "destructive-outline" : "default"}
              onClick={() => setPublishModal(true)}
            >
              {isProgramPublished ? "Unpublish Program" : "Publish Program"}
            </Button>
          )}
        </div>
      </div>

      {/* unpublished banner */}
      {!isProgramPublished && (
        <div className="flex items-center gap-[8px] rounded-[8px] bg-[#FFFBEB] px-[16px] py-[12px]">
          <AlertCircle className="size-[18px] text-[#D97706] shrink-0" />
          <p className="text-[14px] text-[#92400E]">
            The referral program is currently unpublished. Users can not
            generate or use referral links
          </p>
        </div>
      )}

      {/* stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-[16px]">
        {statCards.map((stat) => (
          <StatCard key={stat.label} {...stat} />
        ))}
      </div>

      {/* chart + reward distribution */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-[16px] items-stretch">
        <div className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[16px] font-bold text-[#1E1E1E]">
                Referral Trend
              </p>
              <p className="text-[13px] text-[#758089] mt-[2px]">
                Referral earnings over time
              </p>
            </div>
            <select
              className="h-[36px] rounded-[8px] border border-[#E4E4E7] bg-white px-[12px] text-[13px] text-[#3F3F46] outline-none cursor-pointer"
              value={earningsInterval}
              onChange={(e) =>
                setEarningsInterval(
                  e.target.value as ReferralEarningsChartInterval,
                )
              }
            >
              {EARNINGS_INTERVAL_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={{ height: "320px" }} className="mt-[16px]">
            {isEarningsChartLoading ? (
              <Skeleton height="100%" />
            ) : (
              <Line
                data={{
                  labels: earningsChartLabels,
                  datasets: [
                    {
                      data: earningsChartAmounts,
                      borderColor: "#2E3192",
                      borderWidth: 2,
                      pointRadius: 0,
                      fill: true,
                      tension: 0.4,
                      backgroundColor: (context) => {
                        const { chart } = context;
                        const { ctx, chartArea } = chart;
                        if (!chartArea) return "rgba(46, 49, 146, 0.15)";
                        const gradient = ctx.createLinearGradient(
                          0,
                          chartArea.top,
                          0,
                          chartArea.bottom,
                        );
                        gradient.addColorStop(0, "rgba(46, 49, 146, 0.30)");
                        gradient.addColorStop(1, "rgba(46, 49, 146, 0)");
                        return gradient;
                      },
                    },
                  ],
                }}
                options={{
                  maintainAspectRatio: false,
                  responsive: true,
                  plugins: {
                    legend: { display: false },
                    tooltip: { enabled: true },
                  },
                  scales: {
                    x: {
                      grid: { display: false },
                    },
                    y: {
                      beginAtZero: true,
                      grid: { color: "#F0F0F0" },
                      ticks: {
                        callback: (value) =>
                          RazorNumberFormat(value, {
                            hideDecimal: true,
                            hideSymbol: true,
                            numberAbbreviate: true,
                          }),
                      },
                    },
                  },
                }}
              />
            )}
          </div>
        </div>

        <div className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
          <p className="text-[16px] font-bold text-[#1E1E1E]">
            Reward Distribution
          </p>
          <p className="text-[13px] text-[#758089] mt-[2px]">
            By currency this month
          </p>
          <div className="mt-[20px] space-y-[16px]">
            {isRewardDistributionLoading ? (
              <Skeleton count={3} height={30} />
            ) : rewardDistributionRows.length < 1 ? (
              <p className="text-[13px] text-[#A1A1AA]">
                No reward distribution data yet
              </p>
            ) : (
              rewardDistributionRows.map((row) => (
                <div key={row.currency}>
                  <div className="flex items-center justify-between text-[14px]">
                    <span className="font-medium text-[#3F3F46]">
                      {row.currency}
                    </span>
                    <span className="font-semibold text-[#1E1E1E]">
                      {row.amount}
                    </span>
                  </div>
                  <div className="mt-[8px] h-[6px] w-full rounded-full bg-[#F4F4F5]">
                    <div
                      className="h-[6px] rounded-full"
                      style={{
                        width: `${row.widthPercent}%`,
                        backgroundColor: row.color,
                      }}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* filter + table section */}
      <FilterReusableComp
        searchPlaceholder="Search for referrer or referred user"
        hideExport
        onSearch={(value) => {
          setSearch(value);
          goToFirstPage();
        }}
        defaultSelectTwo="Status"
        selectTwoList={REFERRAL_STATUS_SELECT_LIST.filter(
          (s) => s.label !== filterStatus?.label,
        )}
        selectTwoValue={filterStatus}
        onSelectTwo={(param) => {
          setFilterStatus(param);
          goToFirstPage();
        }}
        moreComponents={
          <DateRangeComponent
            value={dateRange}
            onSet={(value) => {
              setDateRange(value);
              goToFirstPage();
            }}
            onCancel={() => {
              setDateRange("");
              goToFirstPage();
            }}
          />
        }
      />
      <div className="table-section-box">
        {isInitialReferralLoad ? (
          <NoContentPage loading />
        ) : !loading_referrals && referralRows.length < 1 ? (
          <NoContentPage
            title="No referrals found"
            text="Referrals will show up here once users start generating them"
          />
        ) : (
          <>
            <RazorTable
              headerList={[
                "Referrer",
                "Referred User",
                "Reward Amount",
                "Status",
                "Date & Time",
              ]}
              className="mt-[24px]"
            >
              {referralRows.map((row) => (
                <RazorTableRow
                  key={row?.id}
                  loading={loading_referrals}
                  one={
                    `${row?.referrer?.first_name || ""} ${row?.referrer?.last_name || ""}`.trim() ||
                    "---"
                  }
                  two={
                    `${row?.referee?.first_name || ""} ${row?.referee?.last_name || ""}`.trim() ||
                    "---"
                  }
                  three={RazorNumberFormat(row?.referrer_reward_amount || 0, {
                    symbol: row?.currency_code,
                  })}
                  four={
                    <RazorBadgeTwo
                      className={
                        REFERRAL_STATUS_BADGE[row?.status]?.badgeClass || ""
                      }
                      text={
                        REFERRAL_STATUS_BADGE[row?.status]?.label ||
                        row?.status ||
                        "---"
                      }
                    />
                  }
                  five={formatTime(row?.created_at)}
                />
              ))}
            </RazorTable>
            <PaginationFragment
              currentPage={referrals?.meta?.current_page || page}
              perPage={referrals?.meta?.per_page || REFERRALS_PER_PAGE}
              totalPage={referrals?.meta?.total || 0}
              hidePagination={loading_referrals}
              onPageChange={(num) => setPage(num)}
            />
          </>
        )}
      </div>

      <ReuseModal
        visible={publishModal}
        onClose={() => setPublishModal(false)}
        onBtnClick={handleTogglePublish}
        loading={loading_publish_toggle}
        btnLabel={isProgramPublished ? "Unpublish" : "Publish"}
        title={
          isProgramPublished
            ? "Unpublish Referral Program"
            : "Publish Referral Program"
        }
      >
        <p className="delete-text">
          {isProgramPublished
            ? "Are you sure you want to unpublish the referral program? Once unpublished, users will no longer be able to generate or use referral links."
            : "Are you sure you want to publish the referral program? Once published, users will be able to generate and use referral links."}
        </p>
      </ReuseModal>
    </div>
  );
}
